import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { pdfWorking, pdfStatusLabel } from "../hooks/useProposalPdfs";
import { toast } from "sonner";

export const ProposalPdfArchive = ({ leadId, onSaved }) => {
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const saved = useRef(onSaved);
  saved.current = onSaved;
  const readyKey = useRef("");
  const load = useCallback(async () => {
    const { data } = await api.get(`/leads/${leadId}/proposal-pdfs`);
    setJobs(data); setError("");
    const key = data.filter((j) => j.status === "ready").map((j) => j.file_id).join(",");
    if (key !== readyKey.current) { readyKey.current = key; saved.current?.(); }
  }, [leadId]);
  useEffect(() => {
    const refresh = () => load().catch(() => setError("Could not refresh proposal PDF progress."));
    refresh(); const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [load]);
  const retry = async (job) => {
    setBusy(job.id);
    try { await api.post(`/quotes/${job.quote_id}/proposal-pdfs`, { scope: job.scope }); await load(); toast.success("Proposal PDF queued"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  const pending = jobs.filter((job) => pdfWorking(job.status) || job.status === "failed");
  if (!pending.length && !error) return null;
  return <section className="space-y-2 mb-4" data-testid="proposal-pdf-archive">
    <h3 className="font-semibold text-sm" data-testid="proposal-pdf-archive-heading">Proposal PDFs</h3>
    {error && <p className="text-sm text-destructive" data-testid="proposal-pdf-archive-error">{error}</p>}
    {pending.map((job) => <div key={job.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-border py-3" data-testid={`proposal-job-${job.id}`}>
      <div className="min-w-0 flex-1 break-words text-sm">
        <div className="font-semibold" data-testid={`proposal-job-label-${job.id}`}>{job.quote_no} · {job.label}</div>
        <div className="flex items-center gap-2 text-muted-foreground mt-1" data-testid={`proposal-job-status-${job.id}`}>
          {pdfWorking(job.status) && <Loader2 size={13} className="animate-spin shrink-0" />}{pdfStatusLabel(job.status)}
        </div>
        {job.error && <p className="text-xs text-amber-700 mt-1" data-testid={`proposal-job-error-${job.id}`}>{job.error}</p>}
      </div>
      {job.status === "failed" && <button onClick={() => retry(job)} disabled={busy === job.id} data-testid={`proposal-job-retry-${job.id}`}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm hover:bg-muted transition-colors disabled:opacity-50"><RefreshCw size={14} /> Retry</button>}
    </div>)}
  </section>;
};